import type { CSSProperties, ReactNode } from "react";
import { LogoLockup, LogoMark } from "@/components/brand/logo";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

type AuthVariant = "full" | "compact" | "minimal";

const GRID_PATTERN: CSSProperties = {
  backgroundImage:
    "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
  backgroundSize: "40px 40px",
  maskImage:
    "radial-gradient(ellipse 90% 80% at 30% 20%, #000 20%, transparent 95%)",
  WebkitMaskImage:
    "radial-gradient(ellipse 90% 80% at 30% 20%, #000 20%, transparent 95%)",
};

const FULL_TRUST = [
  "Works offline — syncs when you land",
  "Ask AI builds your deal types",
  "Free plan forever, no credit card",
];

const COMPACT_TRUST = ["2-minute setup", "AI builds your first deal types"];

function TrustRow({ children, dot = "h-[5px] w-[5px]" }: { children: ReactNode; dot?: string }) {
  return (
    <div className="flex items-center gap-2 text-xs text-[#a1a1aa]">
      <span className={cn("rounded-full bg-[#fafafa]", dot)} />
      {children}
    </div>
  );
}

function FullBrandPanel() {
  return (
    <aside
      data-testid="auth-brand-panel"
      className="relative hidden overflow-hidden bg-[#0e0e11] p-11 pb-9 text-[#fafafa] md:flex md:flex-col"
    >
      <div className="pointer-events-none absolute inset-0" style={GRID_PATTERN} />
      <div className="pointer-events-none absolute -bottom-40 -left-28 h-[420px] w-[460px] bg-[radial-gradient(closest-side,rgba(255,255,255,0.063),transparent_70%)]" />

      <div className="relative">
        <LogoLockup size={28} />
      </div>

      <div className="relative mt-auto">
        <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#a1a1aa]">
          The CRM
        </div>
        <div className="mt-3 text-[38px] font-semibold leading-[1.1] tracking-[-0.035em]">
          Keep everything.
          <br />
          Nothing slows
          <br />
          down.
        </div>
        <div className="mt-4 flex flex-col gap-[7px]">
          {FULL_TRUST.map((line) => (
            <TrustRow key={line}>{line}</TrustRow>
          ))}
        </div>
      </div>

      <div className="relative mt-7 border-t border-white/[0.07] pt-4 text-[11px] text-[#52525b]">
        © 2026 LeanGrid · app.getleangrid.com
      </div>
    </aside>
  );
}

function CompactBrandPanel() {
  return (
    <aside
      data-testid="auth-brand-panel"
      className="relative hidden overflow-hidden bg-[#0e0e11] p-5 text-[#fafafa] md:flex md:flex-col"
    >
      <div className="pointer-events-none absolute inset-0" style={GRID_PATTERN} />
      <div className="relative">
        <LogoMark size={20} />
      </div>
      <div className="relative mt-auto">
        <div className="text-[17px] font-semibold leading-tight tracking-[-0.02em]">
          Start before your coffee gets cold.
        </div>
        <div className="mt-2.5 flex flex-col gap-[5px]">
          {COMPACT_TRUST.map((line) => (
            <TrustRow key={line} dot="h-1 w-1">
              {line}
            </TrustRow>
          ))}
        </div>
      </div>
    </aside>
  );
}

export function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="w-full max-w-md md:rounded-xl md:border md:bg-card md:p-6 md:shadow-sm">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      <div className="mt-6 flex flex-col gap-3.5">{children}</div>
    </div>
  );
}

export function AuthShell({
  children,
  variant = "full",
  className,
}: {
  children: ReactNode;
  variant?: AuthVariant;
  className?: string;
}) {
  const isMd = useMediaQuery("(min-width: 768px)");

  if (variant === "minimal") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-muted px-4 py-10">
        <div className="mb-4 text-foreground">
          <LogoLockup size={26} />
        </div>
        <div className={cn("w-full max-w-md", className)}>{children}</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid min-h-screen bg-muted",
        variant === "compact"
          ? "md:grid-cols-[220px_1fr]"
          : "md:grid-cols-[520px_1fr]",
      )}
    >
      {isMd && (variant === "compact" ? <CompactBrandPanel /> : <FullBrandPanel />)}
      <main className={cn("flex flex-col items-center justify-center px-4 py-10", className)}>
        {!isMd && (
          <div data-testid="auth-mobile-header" className="md:hidden">
            <LogoLockup size={24} />
          </div>
        )}
        {children}
        {!isMd && (
          <div className="mt-6 w-full max-w-md border-t pt-4 text-xs text-muted-foreground md:hidden">
            Keep everything. Nothing slows down.
          </div>
        )}
      </main>
    </div>
  );
}
